import { cartMutation } from '@theme/cart-mutation';
/**
 * Cart Upsell Component
 * Handles fetching and displaying upsell products in the cart drawer
 */
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';
import { sectionRenderer } from '@theme/section-renderer';

class CartUpsellSlider {
  constructor(element, { prevButton, nextButton, spaceBetween = 12, speed = 300 } = {}) {
    this.element = element;
    this.wrapper = element.querySelector('.cart-upsell-slider-wrapper');
    this.prevButton = prevButton;
    this.nextButton = nextButton;
    this.spaceBetween = spaceBetween;
    this.speed = speed;
    this.index = 0;
    this.slides = [];
    this.boundOnResize = () => this.update();
    this._onPrevClick = () => this.slidePrev();
    this._onNextClick = () => this.slideNext();
    this._onPointerDown = (event) => this.onPointerDown(event);
    this._onPointerMove = (event) => this.onPointerMove(event);
    this._onPointerUp = (event) => this.onPointerUp(event);
    this._onPointerCancel = (event) => this.onPointerCancel(event);
    this._onClickCapture = (event) => this.onClickCapture(event);
    this._onDragStart = (event) => this.onDragStart(event);
    this._onTransitionEnd = (event) => this.onTransitionEnd(event);
    this.dragState = null;
    this.ignoreClicksUntil = 0;
    this.pendingLoopReset = null;
    this.loopResetTimer = null;
    this.dragDisabledElements = new Map();
    this.originalTouchAction = this.element.style.touchAction;
    this.element.style.touchAction = 'pan-y pinch-zoom';

    this.refreshSlides();
    this.bindEvents();
    this.observeResize();
    this.update(false);
  }

  refreshSlides() {
    if (!this.wrapper) {
      this.slides = [];
      return;
    }

    this.wrapper.querySelectorAll('[data-cart-upsell-loop-clone]').forEach((clone) => clone.remove());
    this.slides = [...this.wrapper.querySelectorAll('.cart-upsell-slider-slide:not([data-cart-upsell-loop-clone])')].filter(
      (slide) => slide.style.display !== 'none' && slide.style.visibility !== 'hidden'
    );
    this.disableNativeMediaDrag();
  }

  get isBeginning() {
    return this.index <= 0;
  }

  get isEnd() {
    return this.index >= this.getMaxIndex();
  }

  getOffsetForIndex(index) {
    let offset = 0;
    for (let i = 0; i < index; i++) {
      offset += this.slides[i].offsetWidth + this.spaceBetween;
    }
    return offset;
  }

  getMaxIndex() {
    if (!this.slides.length) return 0;

    const viewWidth = this.element.offsetWidth;
    if (viewWidth <= 0) return 0;

    const totalWidth = this.slides.reduce(
      (sum, slide, index) => sum + slide.offsetWidth + (index < this.slides.length - 1 ? this.spaceBetween : 0),
      0
    );

    if (totalWidth <= viewWidth) return 0;

    for (let i = this.slides.length - 1; i >= 0; i--) {
      const offset = this.getOffsetForIndex(i);
      const slideEnd = offset + this.slides[i].offsetWidth;
      if (slideEnd > viewWidth) {
        return i;
      }
    }

    return 0;
  }

  bindEvents() {
    this.prevButton?.addEventListener('click', this._onPrevClick);
    this.nextButton?.addEventListener('click', this._onNextClick);
    this.element.addEventListener('pointerdown', this._onPointerDown, { passive: true });
    this.element.addEventListener('pointermove', this._onPointerMove, { passive: false });
    this.element.addEventListener('pointerup', this._onPointerUp);
    this.element.addEventListener('pointercancel', this._onPointerCancel);
    this.element.addEventListener('click', this._onClickCapture, true);
    this.element.addEventListener('dragstart', this._onDragStart, true);
    this.wrapper?.addEventListener('transitionend', this._onTransitionEnd);
    window.addEventListener('resize', this.boundOnResize);
  }

  disableNativeMediaDrag() {
    this.element.querySelectorAll('.cart-upsell-product-card__image, .cart-upsell-product-card__image img').forEach((media) => {
      if (this.dragDisabledElements.has(media)) return;
      this.dragDisabledElements.set(media, {
        draggable: media.getAttribute('draggable'),
        webkitUserDrag: media.style.webkitUserDrag,
        userSelect: media.style.userSelect,
      });
      media.setAttribute('draggable', 'false');
      media.style.webkitUserDrag = 'none';
      media.style.userSelect = 'none';
    });
  }

  onDragStart(event) {
    if (event.target instanceof Element && event.target.closest('.cart-upsell-product-card__image')) {
      event.preventDefault();
    }
  }

  isInteractiveTarget(target) {
    return target instanceof Element && Boolean(
      target.closest('button, input, select, textarea, label, [role="button"]')
    );
  }

  onPointerDown(event) {
    const isSupportedPointer =
      event.pointerType === 'touch' ||
      event.pointerType === 'pen' ||
      (event.pointerType === 'mouse' && event.button === 0);
    if (!isSupportedPointer || event.isPrimary === false || this.isInteractiveTarget(event.target)) return;

    this.dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startedAt: performance.now(),
      axis: null,
    };
    this.element.setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event) {
    const state = this.dragState;
    if (!state || state.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - state.startX;
    const deltaY = event.clientY - state.startY;
    if (!state.axis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 6) {
      state.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
    }
    if (state.axis !== 'horizontal') return;

    event.preventDefault();
    const maxOffset = this.getOffsetForIndex(this.getMaxIndex());
    let offset = this.getOffsetForIndex(this.index) - deltaX;
    if (offset < 0) offset *= 0.25;
    if (offset > maxOffset) offset = maxOffset + (offset - maxOffset) * 0.25;
    this.wrapper.style.transition = 'none';
    this.wrapper.style.transform = `translate3d(-${offset}px, 0, 0)`;
  }

  onPointerUp(event) {
    const state = this.dragState;
    if (!state || state.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - state.startX;
    const elapsed = Math.max(performance.now() - state.startedAt, 1);
    const isSwipe = state.axis === 'horizontal' && (Math.abs(deltaX) >= 18 || Math.abs(deltaX) / elapsed >= 0.15);
    this.dragState = null;
    if (this.element.hasPointerCapture?.(event.pointerId)) {
      this.element.releasePointerCapture(event.pointerId);
    }

    if (isSwipe) {
      this.ignoreClicksUntil = performance.now() + 400;
      if (deltaX < 0) this.slideNext();
      else this.slidePrev();
    } else {
      this.applyTransform(true);
    }
  }

  onPointerCancel(event) {
    if (!this.dragState || this.dragState.pointerId !== event.pointerId) return;
    this.dragState = null;
    this.applyTransform(true);
  }

  onClickCapture(event) {
    if (performance.now() < this.ignoreClicksUntil) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  createLoopClone(source) {
    const clone = source.cloneNode(true);
    clone.dataset.cartUpsellLoopClone = 'true';
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    clone.style.pointerEvents = 'none';
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('button, input, select, textarea').forEach((control) => {
      control.disabled = true;
      control.removeAttribute('name');
    });
    clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
    return clone;
  }

  getLoopTargetOffset() {
    return this.slides.reduce((sum, slide) => sum + slide.offsetWidth + this.spaceBetween, 0);
  }

  applyOffset(offset, animate = true) {
    if (!this.wrapper) return;
    this.wrapper.style.transition = animate ? `transform ${this.speed}ms ease` : 'none';
    this.wrapper.style.transform = `translate3d(-${offset}px, 0, 0)`;
  }

  animateLoop(direction) {
    if (!this.wrapper || this.pendingLoopReset || this.slides.length < 2) return;

    const isNext = direction === 'next';
    const source = isNext ? this.slides[0] : this.slides[this.slides.length - 1];
    const clone = this.createLoopClone(source);
    const settleIndex = isNext ? 0 : this.getMaxIndex();
    this.pendingLoopReset = { clone, settleIndex };
    this.ignoreClicksUntil = performance.now() + this.speed + 200;

    if (isNext) {
      this.wrapper.append(clone);
      this.applyOffset(this.getLoopTargetOffset(), true);
    } else {
      this.wrapper.prepend(clone);
      const cloneStep = clone.offsetWidth + this.spaceBetween;
      this.applyOffset(cloneStep, false);
      void this.wrapper.offsetWidth;
      requestAnimationFrame(() => this.applyOffset(0, true));
    }

    clearTimeout(this.loopResetTimer);
    this.loopResetTimer = setTimeout(() => this.finishLoopReset(), this.speed + 100);
  }

  onTransitionEnd(event) {
    if (event.target === this.wrapper && event.propertyName === 'transform' && this.pendingLoopReset) {
      this.finishLoopReset();
    }
  }

  finishLoopReset() {
    if (!this.pendingLoopReset) return;
    clearTimeout(this.loopResetTimer);
    this.loopResetTimer = null;
    const { clone, settleIndex } = this.pendingLoopReset;
    this.pendingLoopReset = null;
    clone.remove();
    this.index = settleIndex;
    this.applyTransform(false);
    this.updateButtonStates();
  }

  observeResize() {
    if (typeof ResizeObserver === 'undefined') return;

    this.resizeObserver = new ResizeObserver(() => {
      this.update();
    });
    this.resizeObserver.observe(this.element);
  }

  applyTransform(animate = true) {
    this.applyOffset(this.getOffsetForIndex(this.index), animate);
  }

  slideTo(index) {
    if (this.pendingLoopReset) return;
    const maxIndex = this.getMaxIndex();
    if (maxIndex > 0 && index > maxIndex) {
      this.animateLoop('next');
      return;
    }
    if (maxIndex > 0 && index < 0) {
      this.animateLoop('prev');
      return;
    }
    this.index = Math.max(0, Math.min(index, maxIndex));
    this.applyTransform(true);
    this.updateButtonStates();
  }

  slideNext() {
    this.slideTo(this.index + 1);
  }

  slidePrev() {
    this.slideTo(this.index - 1);
  }

  update(animate = true) {
    this.finishLoopReset();
    this.refreshSlides();
    if (this.index > this.getMaxIndex()) {
      this.index = this.getMaxIndex();
    }
    this.applyTransform(animate);
    this.updateButtonStates();
  }

  updateButtonStates() {
    const canLoop = this.getMaxIndex() > 0 && this.slides.length > 1;
    if (this.prevButton) {
      this.prevButton.disabled = canLoop ? false : this.isBeginning;
    }
    if (this.nextButton) {
      this.nextButton.disabled = canLoop ? false : this.isEnd;
    }
  }

  destroy() {
    window.removeEventListener('resize', this.boundOnResize);
    this.prevButton?.removeEventListener('click', this._onPrevClick);
    this.nextButton?.removeEventListener('click', this._onNextClick);
    this.element.removeEventListener('pointerdown', this._onPointerDown);
    this.element.removeEventListener('pointermove', this._onPointerMove);
    this.element.removeEventListener('pointerup', this._onPointerUp);
    this.element.removeEventListener('pointercancel', this._onPointerCancel);
    this.element.removeEventListener('click', this._onClickCapture, true);
    this.element.removeEventListener('dragstart', this._onDragStart, true);
    this.wrapper?.removeEventListener('transitionend', this._onTransitionEnd);
    this.element.style.touchAction = this.originalTouchAction;
    this.dragState = null;
    clearTimeout(this.loopResetTimer);
    this.pendingLoopReset?.clone.remove();
    this.pendingLoopReset = null;
    this.resizeObserver?.disconnect();
    this.dragDisabledElements.forEach((original, media) => {
      if (original.draggable === null) media.removeAttribute('draggable');
      else media.setAttribute('draggable', original.draggable);
      media.style.webkitUserDrag = original.webkitUserDrag;
      media.style.userSelect = original.userSelect;
    });
    this.dragDisabledElements.clear();
    if (this.wrapper) {
      this.wrapper.style.transition = '';
      this.wrapper.style.transform = '';
    }
  }
}

class CartUpsellComponent extends HTMLElement {
  get #cartJsUrl() {
    return (typeof Theme !== 'undefined' && Theme?.routes?.cart_url)
      ? Theme.routes.cart_url.replace(/\/?$/, '') + '.js'
      : '/cart.js';
  }

  get addLabel() {
    return this.dataset.labelAdd || 'Add';
  }

  get soldOutLabel() {
    return this.dataset.labelSoldOut || 'Sold out';
  }

  get unavailableLabel() {
    return this.dataset.labelUnavailable || 'Unavailable';
  }

  constructor() {
    super();
    // Use event delegation - bind once, works for all buttons
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.sliderInstance = null;
    this._isLoading = false;
    this._isInitialized = false;
    this._initQueued = false;
    this._dynamicReloadQueued = false;
    this._wasDisconnected = false;
    this._upsellAnalyticsObserver = null;
    this._upsellImpressionPending = false;
    this._upsellImpressionFingerprint = null;
    this._upsellRetryCount = 0;
    this._handleVisitorConsent = () => this.trackUpsellImpressionIfVisible();
    this._handleDrawerOpen = () => {
      if (!this.closest('cart-drawer-component')) return;
      requestAnimationFrame(() => {
        if (this._sliderEnabled) this.sliderInstance?.update();
        this.trackUpsellImpressionIfVisible();
      });
    };
  }

  connectedCallback() {
    // Reset initialization flag when reconnected (e.g., after section morphing)
    // This ensures the component can properly re-initialize after cart updates
    if (this._wasDisconnected) {
      this._isInitialized = false;
      this._isLoading = false;
      this._wasDisconnected = false;
    }
    
    this.queueInit();
    
    // Listen for cart updates to re-setup buttons if needed
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);

    this.observeDynamicUpsellMorphs();
    this.setupUpsellAnalytics();
    document.addEventListener(DialogOpenEvent.eventName, this._handleDrawerOpen, { capture: true });
    document.addEventListener('visitorConsentCollected', this._handleVisitorConsent);
    
    // After a delay, check if we're stuck in loading state and force a reload
    // This is a safety net for cases where init() didn't properly trigger loading
    setTimeout(() => {
      this.checkAndFixLoadingState();
    }, 1000);
  }

  setupUpsellAnalytics() {
    if (this._upsellAnalyticsObserver || typeof IntersectionObserver === 'undefined') return;

    this._upsellAnalyticsObserver = new IntersectionObserver(() => {
      this.trackUpsellImpressionIfVisible();
    }, { threshold: 0.25 });
    this._upsellAnalyticsObserver.observe(this);
  }

  getUpsellAnalyticsContext() {
    const intent = this.dataset.intent;
    const recommendationSource = this.dataset.manualProducts
      ? 'manual'
      : ['related', 'complementary'].includes(intent) ? intent : 'unknown';
    return {
      upsell_location: this.closest('cart-drawer-component') ? 'cart_drawer' : 'cart_page',
      recommendation_source: recommendationSource,
      source_product_id: this.dataset.productId || null,
      currency: window.Shopify?.currency?.active || 'AED'
    };
  }

  getUpsellAnalyticsItems() {
    return Array.from(this.querySelectorAll('.cart-upsell-product-card')).map((card, index) => {
      const button = card.querySelector('.cart-upsell-product-card__button');
      const variantId = button?.dataset.variantId || button?.dataset.initialVariantId || card.dataset.initialVariantId;
      const productId = button?.dataset.productId || card.dataset.productId;
      const priceCents = Number(button?.dataset.priceCents || card.dataset.priceCents || 0);

      return {
        product_id: productId?.toString(),
        variant_id: variantId?.toString(),
        price_cents: Number.isFinite(priceCents) ? Math.max(0, Math.round(priceCents)) : 0,
        quantity: 1,
        position: index
      };
    }).filter((item) => item.product_id && item.variant_id);
  }

  createUpsellEventId() {
    if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
      const random = window.crypto.getRandomValues(new Uint8Array(1))[0] & 15;
      const value = character === 'x' ? random : (random & 3) | 8;
      return value.toString(16);
    });
  }

  isUpsellAnalyticsAllowed() {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch (_) {
      return false;
    }
  }

  getUpsellVisitorId() {
    const storageKey = 'sp_upsell_visitor_id';
    const now = Date.now();
    try {
      const stored = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
      if (stored?.id && Number(stored.expires) > now) return stored.id;
      const visitorId = this.createUpsellEventId();
      window.localStorage.setItem(storageKey, JSON.stringify({ id: visitorId, expires: now + 86400000 }));
      return visitorId;
    } catch (_) {
      return null;
    }
  }

  async requestUpsellAttribution(eventId, items) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2000);
    try {
      const cartResponse = await fetch(this.#cartJsUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal
      });
      if (!cartResponse.ok) throw new Error('Cart token unavailable');
      const cart = await cartResponse.json();
      const cartToken = String(cart.token || '').split('?')[0];
      if (!cartToken) throw new Error('Cart token unavailable');
      const location = this.closest('cart-drawer-component') ? 'cart_drawer' : 'cart_page';
      const tokenResponse = await fetch('https://analytics.satori-io.com/v1/tokens', {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          event_id: eventId,
          cart_token: cartToken,
          items: items.map((item) => ({ product_id: item.product_id, variant_id: item.variant_id })),
          upsell_location: location,
          recommendation_source: this.getUpsellAnalyticsContext().recommendation_source,
          source_product_id: this.dataset.productId || null
        })
      });
      if (!tokenResponse.ok) throw new Error('Attribution token unavailable');
      const tokenData = await tokenResponse.json();
      if (!tokenData.token || !tokenData.cart_binding) throw new Error('Attribution token unavailable');
      const cartUpdateUrl = Theme.routes.cart_update_url || `${window.Shopify?.routes?.root || '/'}cart/update.js`;
      const bindingResponse = await fetch(cartUpdateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({ attributes: { __sp_cart_binding: tokenData.cart_binding } })
      });
      if (!bindingResponse.ok) throw new Error('Cart binding unavailable');
      return {
        event_id: eventId,
        cart_token: cartToken,
        cart_binding: tokenData.cart_binding,
        attribution_token: tokenData.token,
        upsell_location: location
      };
    } finally {
      clearTimeout(timeout);
    }
  }

  async publishUpsellAnalyticsEvent(eventName, eventData = {}) {
    if (!this.isUpsellAnalyticsAllowed()) return false;
    const visitorId = this.getUpsellVisitorId();
    const items = eventData.items || [];
    if (!visitorId || items.length === 0) return false;

    try {
      const eventId = eventData.attribution?.event_id || eventData.event_id || this.createUpsellEventId();
      const attribution = eventData.attribution || await this.requestUpsellAttribution(eventId, items);
      const payload = {
        event_id: eventId,
        visitor_id: visitorId,
        event_type: eventName,
        ...this.getUpsellAnalyticsContext(),
        cart_token: attribution.cart_token,
        attribution_token: attribution.attribution_token,
        items
      };
      const response = await fetch('https://analytics.satori-io.com/v1/events', {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      return response.ok;
    } catch (_) {
      // Analytics must never interrupt cart behavior.
      return false;
    }
  }

  trackUpsellImpressionIfVisible() {
    const items = this.getUpsellAnalyticsItems();
    const rect = this.getBoundingClientRect();
    const style = window.getComputedStyle(this);
    const isVisible = items.length > 0 &&
      !this.hidden &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > 0 &&
      rect.top < window.innerHeight;

    if (!isVisible || !this.isUpsellAnalyticsAllowed()) return;
    const fingerprint = items.map((item) => item.variant_id).join(',');
    if (this._upsellImpressionPending || this._upsellImpressionFingerprint === fingerprint) return;
    this._upsellImpressionPending = true;
    this.publishUpsellAnalyticsEvent('cart_upsell_impression', { items }).then((sent) => {
      this._upsellImpressionPending = false;
      if (sent) {
        this._upsellImpressionFingerprint = fingerprint;
        this._upsellRetryCount = 0;
      } else if (this.isConnected && this.isUpsellAnalyticsAllowed() && (this._upsellRetryCount || 0) < 3) {
        this._upsellRetryCount = (this._upsellRetryCount || 0) + 1;
        setTimeout(() => this.trackUpsellImpressionIfVisible(), 5000);
      }
    });
  }

  queueInit() {
    if (this._initQueued) return;

    this._initQueued = true;
    requestAnimationFrame(() => {
      this._initQueued = false;
      if (this.isConnected) {
        this.init();
      }
    });
  }
  
  checkAndFixLoadingState() {
    if (!this.isConnected || this._isLoading) {
      return;
    }
    
    const hasProducts = this.querySelectorAll('.cart-upsell-product-card').length > 0;
    const hasLoadingState = this.querySelector('[ref="loadingState"]') !== null;
    const intent = this.dataset.intent;
    const manualProducts = this.dataset.manualProducts;
    
    // If we have loading state but no products, we need to load them
    if (hasLoadingState && !hasProducts && !manualProducts && intent) {
      // Get product ID - try dataset first, then cart API
      const getProductId = async () => {
        let productId = this.dataset.productId;
        
        if (!productId) {
          // Try to get from cart API
          try {
            const response = await fetch(this.#cartJsUrl);
            const cartData = await response.json();
            if (cartData.items && cartData.items.length > 0) {
              productId = cartData.items[0].product_id?.toString();
              // Update dataset for future use
              if (productId) {
                this.dataset.productId = productId;
              }
            }
          } catch (error) {
            console.warn('Could not fetch cart for product ID:', error);
          }
        }
        
        return productId;
      };
      
      getProductId().then((productId) => {
        if (this.isConnected && productId && !this._isLoading) {
          const maxProducts = parseInt(this.dataset.maxProducts || '4', 10);
          this.loadRecommendations(productId, intent, maxProducts);
        }
      });
    }
  }

  observeDynamicUpsellMorphs() {
    if (this.dataset.manualProducts || this._dynamicMorphObserver) return;

    this._dynamicMorphObserver = new MutationObserver(() => {
      this.queueDynamicUpsellReload();
    });

    this._dynamicMorphObserver.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  queueDynamicUpsellReload() {
    if (this.dataset.manualProducts || this._dynamicReloadQueued || this._isLoading) return;

    this._dynamicReloadQueued = true;
    requestAnimationFrame(() => {
      this._dynamicReloadQueued = false;
      if (!this.isConnected || this.dataset.manualProducts || this._isLoading) return;

      const hasProducts = this.querySelectorAll('.cart-upsell-product-card').length > 0;
      const hasContainer =
        this.querySelector('[ref="productsContainer"]') ||
        this.querySelector('.cart-upsell__products') ||
        this.querySelector('.cart-upsell-slider-wrapper');
      const productId = this.dataset.productId;
      const intent = this.dataset.intent;

      if (!hasProducts && hasContainer && productId && intent) {
        const maxProducts = parseInt(this.dataset.maxProducts || '4', 10);
        this.loadRecommendations(productId, intent, maxProducts).finally(() => {
          this._isInitialized = true;
        });
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    document.removeEventListener(DialogOpenEvent.eventName, this._handleDrawerOpen, { capture: true });
    document.removeEventListener('visitorConsentCollected', this._handleVisitorConsent);
    // Clean up event listeners
    if (this._productsContainer) {
      this._productsContainer.removeEventListener('click', this.handleButtonClick);
      this._productsContainer.removeEventListener('change', this.handleVariantChange);
      this._productsContainer = null;
    }
    // Destroy slider instance
    if (this.sliderInstance) {
      this.sliderInstance.destroy();
      this.sliderInstance = null;
    }
    if (this._dynamicMorphObserver) {
      this._dynamicMorphObserver.disconnect();
      this._dynamicMorphObserver = null;
    }
    if (this._upsellAnalyticsObserver) {
      this._upsellAnalyticsObserver.disconnect();
      this._upsellAnalyticsObserver = null;
    }
    this._upsellImpressionPending = false;
    this._upsellImpressionFingerprint = null;
    this._upsellRetryCount = 0;
    // Reset initialization flags so component can re-initialize when reconnected
    this._isInitialized = false;
    this._isLoading = false;
    this._initQueued = false;
    this._dynamicReloadQueued = false;
    // Mark that component was disconnected so we know to reset on reconnect
    this._wasDisconnected = true;
  }

  handleCartUpdate = async (event) => {
    // If this component triggered the update, restore button states based on cart
    if (event.target === this || event.detail?.sourceId === this.id) {
      // After we add a product, verify button states match what's actually in cart
      // Wait a bit for cart to fully update
      setTimeout(() => {
        if (this.isConnected) {
          this.syncButtonStatesWithCart();
        }
      }, 300);
      return;
    }
    // Don't re-setup if component is being destroyed
    if (!this.isConnected) {
      return;
    }

    // When free-shipping-progress removes a gift, morph may leave cart-upsell in loading state.
    // Force re-init so recommendations load correctly.
    if (event.detail?.data?.source === 'free-shipping-progress') {
      this._isInitialized = false;
      this._isLoading = false;
      setTimeout(() => {
        if (this.isConnected && !this._isInitialized) {
          this.init();
        }
      }, 100);
    }
    
    // Get current cart item count only when the event includes it.
    const itemCount = event.detail?.data?.itemCount ?? event.detail?.resource?.item_count;
    
    // If cart is confirmed empty, hide the component.
    if (itemCount === 0) {
      this.style.display = 'none';
      this.setAttribute('hidden', '');
      this.classList.add('cart-upsell--hidden');
      this.updateCartItemsWrapperVisibility(true);
      return;
    }
    
    // Re-setup buttons after cart updates in case DOM changed
    // Use a longer delay to ensure DOM is stable
    setTimeout(() => {
      if (this.isConnected) {
        this.setupAddToCartButtons();
        // Sync button states with what's actually in cart
        this.syncButtonStatesWithCart();
        this.queueDynamicUpsellReload();
      }
    }, 300);
    
    // Check and fix loading state after cart update (important for removal case)
    // This ensures products are loaded even if the component got stuck
    setTimeout(() => {
      this.checkAndFixLoadingState();
    }, 500);
    
    // Update cart items wrapper visibility only when cart count is known.
    if (typeof itemCount === 'number') {
      this.updateCartItemsWrapperVisibility(itemCount === 0);
    }
  }
  
  async updateProductIdIfNeeded() {
    // For complementary/related products, update the product ID based on the first cart item
    const intent = this.dataset.intent;
    if (intent === 'complementary' || intent === 'related') {
      // Try to get the first cart item's product ID from DOM first (synchronous)
      const firstCartItem = document.querySelector('cart-items-component [data-product-id]');
      if (firstCartItem) {
        const newProductId = firstCartItem.dataset.productId;
        if (newProductId && newProductId !== this.dataset.productId) {
          this.dataset.productId = newProductId;
          // Reset initialization flag so component can reload with new product ID
          this._isInitialized = false;
          return newProductId;
        }
        return this.dataset.productId;
      } else {
        // If DOM doesn't have cart items yet, try fetching from cart API
        try {
          const response = await fetch(this.#cartJsUrl);
          const cartData = await response.json();
          if (cartData.items && cartData.items.length > 0) {
            const firstItem = cartData.items[0];
            const newProductId = firstItem.product_id?.toString();
            if (newProductId && newProductId !== this.dataset.productId) {
              this.dataset.productId = newProductId;
              // Reset initialization flag so component can reload with new product ID
              this._isInitialized = false;
              return newProductId;
            }
            return this.dataset.productId;
          }
        } catch (error) {
          // Silently fail - we'll try again later
          console.warn('Could not fetch cart data for product ID update:', error);
        }
      }
    }
    return this.dataset.productId;
  }

  async syncButtonStatesWithCart() {
    if (!this.isConnected) return;

    // Dynamic recommendation upsells should stay stable after render. The
    // recommendation API already filters from the current cart item context,
    // and this sync path can hide freshly rendered cards during cart morphs.
    if (!this.dataset.manualProducts) {
      this.style.display = '';
      this.removeAttribute('hidden');
      this.classList.remove('cart-upsell--hidden');
      return;
    }

    let cartProductIds = new Set();

    try {
      const cartResponse = await fetch(this.#cartJsUrl);
      if (!this.isConnected) return;
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        if (!this.isConnected) return;
        if (cartData.items && Array.isArray(cartData.items)) {
          cartProductIds = new Set(
            cartData.items.map(item => item.product_id?.toString()).filter(Boolean)
          );
        }
      }
    } catch (error) {
      if (this.isConnected) console.warn('Could not fetch cart data:', error);
      return;
    }

    if (!this.isConnected) return;

    // Check each button and sync its state with cart
    const allButtons = this.querySelectorAll('.cart-upsell-product-card__button');
    let hasAvailableProducts = false;
    
    // Also check for manual products data attribute to see what products should be shown
    const manualProducts = this.dataset.manualProducts;
    const manualProductIds = manualProducts ? manualProducts.split(',').filter(Boolean) : [];
    
    // For slider mode, also check slides
    const sliderWrapper = this.querySelector('.cart-upsell-slider-wrapper');
    const slides = sliderWrapper ? sliderWrapper.querySelectorAll('.cart-upsell-slider-slide') : [];
    
    allButtons.forEach(button => {
      const productId = button.dataset.productId?.toString();
      if (!productId) return;
      
      // A pending add owns its button until the request completes.
      if (button.dataset.processing === 'true') return;
      const isInCart = cartProductIds.has(productId);
      
      if (isInCart) {
        // Product is in cart - mark as added
        button.disabled = true;
        button.classList.add('added');
        button.dataset.added = 'true';
        this.setUpsellButtonFeedback(button, 'success');
        const productCard = button.closest('.cart-upsell-product-card');
        if (productCard) {
          productCard.classList.add('product-added');
        }
        
        // Hide the slide in slider mode
        if (this._sliderEnabled) {
          const slide = productCard?.closest('.cart-upsell-slider-slide');
          if (slide) {
            // Hide the slide completely
            slide.style.display = 'none';
            slide.style.visibility = 'hidden';
            // Mark that slides have changed
            this._slidesChanged = true;
            // Update slider after hiding slide
            if (this.sliderInstance) {
              this.sliderInstance.update();
            }
          }
        }
      } else {
        // Product is NOT in cart - ensure button is enabled and reset state
        hasAvailableProducts = true; // Mark that we have at least one available product
        button.disabled = false;
        button.classList.remove('processing', 'added');
        delete button.dataset.processing;
        delete button.dataset.added;
        
        this.setUpsellButtonFeedback(button, 'idle');

        // Restore legacy button HTML if it was modified
        if (button.innerHTML === 'Added!' || button.innerHTML.includes('Added')) {
          button.innerHTML = `
            <span class="svg-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" width="16" height="16">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width, 1.5)" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"/>
                <path stroke="currentColor" stroke-linecap="round" stroke-width="var(--icon-stroke-width, 1.5)" d="M14.608 12.563v5m2.5-2.5h-5"/>
              </svg>
            </span>
            <span class="is-visually-hidden-mobile">${this.addLabel}</span>
          `;
        }
        
        // Remove product-added class
        const productCard = button.closest('.cart-upsell-product-card');
        if (productCard) {
          productCard.classList.remove('product-added');
        }
        
        // Show the slide in slider mode
        if (this._sliderEnabled) {
          const slide = productCard?.closest('.cart-upsell-slider-slide');
          if (slide) {
            // Remove inline display style to show the slide
            slide.style.display = '';
            slide.style.visibility = '';
            slide.style.opacity = '';
            
            // Mark that slides have changed and need reconnection
            this._slidesChanged = true;
            
            // Update slider after showing slide
            if (this.sliderInstance) {
              this.sliderInstance.update();
            }
          }
        }
      }
    });

    // For slider mode, if slides were shown/hidden, reconnect arrows and update Swiper
    if (this._slidesChanged && this._sliderEnabled) {
      // Reset the flag
      this._slidesChanged = false;
      // Reconnect arrows and update slider after slides are restored
      // Use longer delay to ensure slider is ready
      setTimeout(() => {
        this.initSlider();
      }, 250);
    }

    // For slider mode, check if any visible slides remain
    if (this._sliderEnabled) {
      const sliderElement = this.querySelector('.cart-upsell-slider');
      if (sliderElement) {
        const slides = sliderElement.querySelectorAll('.cart-upsell-slider-slide');
        const visibleSlides = Array.from(slides).filter(slide => {
          // A slide is visible if it's not explicitly hidden via style.display
          return slide.style.display !== 'none';
        });
        if (visibleSlides.length === 0) {
          hasAvailableProducts = false;
        } else {
          // If we have visible slides but no available products from buttons, check slides
          if (!hasAvailableProducts) {
            // Check if any visible slide has a product not in cart
            const hasVisibleProduct = visibleSlides.some(slide => {
              const button = slide.querySelector('.cart-upsell-product-card__button');
              if (!button) return false;
              const productId = button.dataset.productId?.toString();
              return productId && !cartProductIds.has(productId);
            });
            if (hasVisibleProduct) {
              hasAvailableProducts = true;
            }
          }
        }
      }
    }

    // Show/hide heading and section based on whether there are available products
    const heading = this.querySelector('.cart-upsell__heading');
    const header = this.querySelector('.cart-upsell__header');
    if (heading) {
      if (hasAvailableProducts) {
        heading.style.display = '';
      } else {
        heading.style.display = 'none';
      }
    }
    if (header) {
      if (hasAvailableProducts) {
        header.style.display = '';
      } else {
        header.style.display = 'none';
      }
    }

    // Check if all manual products are in cart (even if no buttons rendered)
    let allManualProductsInCart = false;
    if (manualProductIds.length > 0) {
      allManualProductsInCart = manualProductIds.every(productId => 
        cartProductIds.has(productId.toString())
      );
    }
    
    // Count products NOT in cart from buttons
    let productsNotInCart = 0;
    allButtons.forEach(button => {
      const productId = button.dataset.productId?.toString();
      if (productId && !cartProductIds.has(productId)) {
        productsNotInCart++;
      }
    });
    
    // Determine if we should hide the component
    // Hide if all products that should be shown are in cart
    let shouldHide = false;
    
    if (manualProductIds.length > 0) {
      // For manual products: hide if all manual products are in cart
      shouldHide = allManualProductsInCart;
    }
    
    if (shouldHide) {
      // All upsell products are in cart - hide the entire component
      this.style.display = 'none';
      this.setAttribute('hidden', '');
      this.classList.add('cart-upsell--hidden');
    } else {
      // Show the component
      this.style.display = '';
      this.removeAttribute('hidden');
      this.classList.remove('cart-upsell--hidden');
      
      // Reconnect slider arrows if slider is enabled and section is now visible
      if (this._sliderEnabled) {
        // Small delay to ensure DOM is updated and slider is ready
        setTimeout(() => {
          // Reinitialize slider
          this.initSlider();
        }, 200);
      }
      
      // Also reconnect if slides were changed during this sync
      if (this._slidesChanged && this._sliderEnabled) {
        setTimeout(() => {
          this.initSlider();
        }, 300);
      }
    }
    
    // Hide cart-items__wrapper when cart is empty (no products left)
    this.updateCartItemsWrapperVisibility(cartProductIds.size === 0);
  }

  updateCartItemsWrapperVisibility(isEmpty) {
    const cartItemsWrapper = document.querySelector('.cart-items__wrapper');
    if (cartItemsWrapper) {
      if (isEmpty) {
        cartItemsWrapper.style.display = 'none';
      } else {
        cartItemsWrapper.style.display = '';
      }
    }
  }

  init() {
    const hasExpectedMarkup =
      this.dataset.manualProducts ||
      this.querySelector('[ref="productsContainer"]') ||
      this.querySelector('.cart-upsell__products') ||
      this.querySelector('.cart-upsell-slider') ||
      this.querySelector('.cart-upsell-slider-wrapper') ||
      this.querySelector('.cart-upsell-product-card');

    if (!hasExpectedMarkup) {
      this.queueInit();
      return;
    }

    // Check if cart is empty first
    const cartItems = document.querySelectorAll('cart-items-component [data-product-id]');
    if (cartItems.length === 0) {
      // Try to check via cart API
      fetch(this.#cartJsUrl)
        .then((response) => (this.isConnected ? response.json() : Promise.reject(new Error('disconnected'))))
        .then((cartData) => {
          if (!this.isConnected) return;
          if (!cartData.items || cartData.items.length === 0) {
            this.style.display = 'none';
            this.setAttribute('hidden', '');
            this.classList.add('cart-upsell--hidden');
            return;
          }
          this._doInit();
        })
        .catch(() => {
          if (this.isConnected) this._doInit();
        });
    } else {
      this._doInit();
    }
  }
  
  _doInit() {
    // Prevent multiple simultaneous initializations
    if (this._isInitialized && this._isLoading) {
      return;
    }
    
    const productId = this.dataset.productId;
    const intent = this.dataset.intent;
    const manualProducts = this.dataset.manualProducts;
    const maxProducts = parseInt(this.dataset.maxProducts || '4', 10);
    const sliderEnabled = this.dataset.sliderEnabled === 'true';

    // Set slider mode flag
    this._sliderEnabled = sliderEnabled;

    // Set up event delegation for add to cart buttons
    this.setupAddToCartButtons();

    // Connect slider arrows if slider is enabled
    if (sliderEnabled) {
      this.connectSliderArrows();
    }

    // Handle manual products
    if (manualProducts) {
      this.loadManualProducts(manualProducts.split(','), maxProducts);
      // Sync button states on initial load for manual products
      setTimeout(() => {
        this.connectSliderArrows();
        this.syncButtonStatesWithCart();
        // Double-check to ensure component is hidden if all products in cart
        setTimeout(() => {
          this.syncButtonStatesWithCart();
        }, 200);
      }, 300);
      this._isInitialized = true;
      return;
    }

    // Handle complementary or related products
    if (productId && intent) {
      // Update product ID based on current cart state (important after cart changes)
      // This is async, so we need to wait for it
      this.updateProductIdIfNeeded().then((currentProductId) => {
        if (!this.isConnected) {
          this._isInitialized = true;
          return;
        }
        
        if (!currentProductId) {
          // No product ID means cart might be empty or component shouldn't show
          this.style.display = 'none';
          this.setAttribute('hidden', '');
          this._isInitialized = true;
          return;
        }
        
        // Check if products are already loaded
        const hasProducts = this.querySelectorAll('.cart-upsell-product-card').length > 0;
        const hasLoadingState = this.querySelector('[ref="loadingState"]') !== null;
        
        // If product ID changed, we need to reload
        const productIdChanged = currentProductId !== productId;
        
        // Only load if we have loading state and no products, or if product ID changed
        if (productIdChanged || (hasLoadingState && !hasProducts)) {
          this.loadRecommendations(currentProductId, intent, maxProducts).finally(() => {
            this._isInitialized = true;
          });
        } else if (!hasProducts && !hasLoadingState) {
          this.loadRecommendations(currentProductId, intent, maxProducts).finally(() => {
            this._isInitialized = true;
          });
        } else {
          // Products are already loaded or component is in a good state
          this._isInitialized = true;
        }
      }).catch(() => {
        // If update fails, try to load with original product ID
        if (this.isConnected && productId) {
          const hasProducts = this.querySelectorAll('.cart-upsell-product-card').length > 0;
          const hasLoadingState = this.querySelector('[ref="loadingState"]') !== null;
          if ((hasLoadingState && !hasProducts) || (!hasProducts && !hasLoadingState)) {
            this.loadRecommendations(productId, intent, maxProducts).finally(() => {
              this._isInitialized = true;
            });
          } else {
            this._isInitialized = true;
          }
        } else {
          this._isInitialized = true;
        }
      });
    } else {
      // Not complementary/related products, mark as initialized
      this._isInitialized = true;
    }
    
    if (manualProducts) {
      setTimeout(() => {
        this.syncButtonStatesWithCart();
      }, 500);
    }
  }

  initSlider() {
    const sliderElement = this.querySelector('.cart-upsell-slider');
    if (!sliderElement) {
      return;
    }

    if (this.sliderInstance) {
      this.sliderInstance.destroy();
      this.sliderInstance = null;
    }

    const prevButton = this.querySelector('[data-slider-nav="previous"]');
    const nextButton = this.querySelector('[data-slider-nav="next"]');

    const spaceBetween =
      parseInt(getComputedStyle(this).getPropertyValue('--cart-upsell-slider-gap'), 10) || 12;

    this.sliderInstance = new CartUpsellSlider(sliderElement, {
      prevButton,
      nextButton,
      spaceBetween,
      speed: 300,
    });

    this.updateSliderButtonStates();
  }

  updateSliderButtonStates() {
    if (!this.sliderInstance) return;
    this.sliderInstance.updateButtonStates();
  }

  connectSliderArrows() {
    this.initSlider();
  }

  async loadRecommendations(productId, intent, maxProducts) {
    // Prevent multiple simultaneous loads
    if (this._isLoading) {
      return;
    }
    
    this._isLoading = true;
    const hadProductsBeforeLoad = this.querySelectorAll('.cart-upsell-product-card').length > 0;
    const loadingState = this.querySelector('[ref="loadingState"]');
    const productsContainer = this.querySelector('[ref="productsContainer"]') || 
                              this.querySelector('.cart-upsell__products') ||
                              this.querySelector('.cart-upsell-slider-wrapper') ||
                              this;

    try {
      // Use Shopify's recommendations API
      const localeRoot = window.Shopify?.routes?.root || '/';
      const requestLimit = this.classList.contains('sp-staff-picks') ? 10 : maxProducts;
      const url = `${localeRoot.replace(/\/?$/, '/')}recommendations/products.json?product_id=${productId}&limit=${requestLimit}&intent=${intent}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (!response.ok) {
        throw new Error(`Failed to fetch recommendations: ${response.status}`);
      }

      const data = await response.json();
      
      // Only proceed if component is still connected
      if (!this.isConnected) {
        this._isLoading = false;
        return;
      }
      
      if (loadingState) {
        loadingState.remove();
      }

      if (data.products && data.products.length > 0) {
        await this.renderProducts(data.products, productsContainer);
        requestAnimationFrame(() => this.trackUpsellImpressionIfVisible());
      } else {
        if (hadProductsBeforeLoad) return;
        if (productsContainer) {
          productsContainer.innerHTML = '';
        }
        this.style.display = 'none';
        this.setAttribute('hidden', '');
      }
    } catch (error) {
      console.error('Error loading cart upsell recommendations:', error);
      // Only update UI if component is still connected
      if (this.isConnected) {
        if (hadProductsBeforeLoad) return;
        if (loadingState) {
          loadingState.remove();
        }
        if (productsContainer) {
          productsContainer.innerHTML = '';
        }
        this.style.display = 'none';
        this.setAttribute('hidden', '');
      }
    } finally {
      this._isLoading = false;
    }
  }

  loadManualProducts(productHandles, maxProducts) {
    // Manual products are already rendered in the Liquid template
    // Event listeners are already set up via event delegation in init()
    const products = this.querySelectorAll('.cart-upsell-product-card');
    
    if (products.length === 0) {
      this.style.display = 'none';
      this.setAttribute('hidden', '');
      return;
    }

    requestAnimationFrame(() => this.trackUpsellImpressionIfVisible());
  }

  async renderProducts(products, container) {
    if (!container) return;

    // Filter out products already in cart
    // Try multiple methods to get cart product IDs since cart might be updating
    let cartProductIds = [];
    
    // Method 1: From cart items component
    const cartItems = document.querySelectorAll('cart-items-component [data-product-id]');
    if (cartItems.length > 0) {
      cartProductIds = Array.from(cartItems).map(el => el.dataset.productId);
    } else {
      // Method 2: Fetch from cart API if DOM elements aren't available yet
      try {
        const cartResponse = await fetch(this.#cartJsUrl);
        if (cartResponse.ok) {
          const cartData = await cartResponse.json();
          if (cartData.items && Array.isArray(cartData.items)) {
            cartProductIds = cartData.items.map(item => item.product_id?.toString()).filter(Boolean);
          }
        }
      } catch (error) {
        console.warn('Could not fetch cart data for filtering:', error);
      }
    }

    const filteredProducts = products.filter(product => {
      const productId = product?.id?.toString();
      const hasAvailableVariant = Boolean(this.getFirstAvailableVariant(product));
      return product?.available === true && hasAvailableVariant && !cartProductIds.includes(productId);
    }).slice(0, parseInt(this.dataset.maxProducts || '4', 10));

    if (filteredProducts.length === 0) {
      if (this.querySelectorAll('.cart-upsell-product-card').length > 0) return;
      container.innerHTML = '';
      this.style.display = 'none';
      this.setAttribute('hidden', '');
      return;
    }

    // Render products directly from JSON data
    // No need for section renderer - we'll render inline
    this.renderProductsManually(filteredProducts, container);
  }

  renderProductsManually(products, container) {
    if (this._sliderEnabled) {
      this.renderProductsAsSlider(products, container);
      return;
    }

    container.innerHTML = products.map((product, index) => {
      return `
        <div class="cart-upsell__product">
          ${this.buildProductCardHTML(product, index)}
        </div>
      `;
    }).join('');

    // Initialize variant selectors for dynamically rendered products
    setTimeout(() => {
      this.initializeVariantSelectors();
      this.syncButtonStatesWithCart();
    }, 100);
  }

  getFirstAvailableVariant(product) {
    if (product?.available !== true || !Array.isArray(product.variants)) return null;

    const selectedVariant = product.selected_or_first_available_variant;
    if (selectedVariant?.available === true) {
      return product.variants.find(variant => variant.id?.toString() === selectedVariant.id?.toString() && variant.available === true)
        || selectedVariant;
    }

    return product.variants.find(variant => variant.available === true) || null;
  }

  renderVariantSelectorHTML(product, productIdSuffix) {
    if (!product.options_with_values || !product.variants) {
      return '';
    }

    const initialVariant = this.getFirstAvailableVariant(product);
    if (!initialVariant) return '';

    const optionsHtml = product.options_with_values.map((option, optIndex) => {
      if (!option.values || option.values.length <= 1) {
        return '';
      }

      const optionsHtml = option.values.map(value => {
        const matchingVariants = product.variants.filter(v => {
          if (optIndex === 0) return v.option1 === value;
          if (optIndex === 1) return v.option2 === value;
          if (optIndex === 2) return v.option3 === value;
          return false;
        });
        const availableMatchingVariant = matchingVariants.find(v => v.available === true);
        const initialValue = initialVariant[`option${optIndex + 1}`];
        const selected = value === initialValue;
        const matchingVariant = selected ? initialVariant : (availableMatchingVariant || matchingVariants[0]);
        const unavailable = !availableMatchingVariant;

        return `
          <option
            value="${this.escapeHtml(value)}"
            ${matchingVariant ? `data-variant-id="${matchingVariant.id}"` : ''}
            ${selected ? 'selected' : ''}
            ${unavailable ? 'disabled' : ''}
          >
            ${this.escapeHtml(value)}${unavailable ? ` - ${this.unavailableLabel}` : ''}
          </option>
        `;
      }).join('');

      return `
        <div class="cart-upsell-variant-selector__option">
          <label class="cart-upsell-variant-selector__label" for="cart-upsell-option-${product.id}-${productIdSuffix}-${option.position}">
            ${this.escapeHtml(option.name)}:
          </label>
          <select
            class="cart-upsell-variant-selector__select"
            id="cart-upsell-option-${product.id}-${productIdSuffix}-${option.position}"
            data-option-position="${option.position}"
            data-option-name="${this.escapeHtml(option.name)}"
          >
            ${optionsHtml}
          </select>
        </div>
      `;
    }).join('');

    const variantsJson = JSON.stringify({
      variants: product.variants.map(v => ({
        id: v.id,
        price: v.price,
        compare_at_price: v.compare_at_price || 0,
        available: v.available === true,
        option1: v.option1 || '',
        option2: v.option2 || '',
        option3: v.option3 || ''
      }))
    });

    return `
      <div class="cart-upsell-variant-selector" data-product-id="${product.id}" data-product-id-suffix="${productIdSuffix}">
        ${optionsHtml}
        <script type="application/json" class="cart-upsell-variant-data">
          ${variantsJson}
        </script>
      </div>
    `;
  }

  escapeHtml(text) {
    if (text == null) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  buildProductCardHTML(product, index) {
    const variant = this.getFirstAvailableVariant(product);
    if (!variant) return '';

    const image = product.featured_image || product.images?.[0] || product.featured_media?.preview_image;
    const price = variant.price || 0;
    const compareAtPrice = variant.compare_at_price > variant.price ? variant.compare_at_price : null;
    const available = variant.available === true;
    const hasVariants = product.variants && product.variants.length > 1;
    const hasOptions = product.options && product.options.length > 0;

    const productTitle = this.escapeHtml(product.title);
    const localeRoot = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const productUrl = product.url || `${localeRoot}products/${product.handle}`;
    const imageUrl = image ? (typeof image === 'string' ? image : image.src || image.url) : null;
    const imageAlt = image?.alt || productTitle;
    const imageRatio = this.getImageRatio(product);
    const imageSizes = this.cartThumbSizes(imageUrl);

    const addToCartIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" width="16" height="16">
        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width, 1.5)" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"/>
        <path stroke="currentColor" stroke-linecap="round" stroke-width="var(--icon-stroke-width, 1.5)" d="M14.608 12.563v5m2.5-2.5h-5"/>
      </svg>
    `;

    const priceHtml = compareAtPrice
      ? `
        <div class="cart-upsell-product-card__price" data-price-container>
          <span class="cart-upsell-product-card__price-current cart-secondary-typography" data-price-current>${this.formatPriceSplit(price)}</span>
          <s class="cart-upsell-product-card__price-compare cart-secondary-typography" data-price-compare><span class="cart-upsell-product-card__compare-inner">${this.formatPriceSplit(compareAtPrice)}</span></s>
        </div>
      `
      : `
        <div class="cart-upsell-product-card__price cart-upsell-product-card__price--no-compare" data-price-container>
          <span class="cart-upsell-product-card__price-current cart-secondary-typography" data-price-current>${this.formatPriceSplit(price)}</span>
        </div>
      `;

    const actionHtml = available && variant
      ? hasVariants && hasOptions && product.variants && product.variants.length > 1
        ? `
          <button type="button" class="button cart-upsell-product-card__button"
            data-product-id="${product.id}"
            data-product-handle="${product.handle || ''}"
            data-product-title="${productTitle}"
            data-initial-variant-id="${variant.id}"
            data-price-cents="${price}"
            data-has-variants="true">
            <span class="svg-wrapper">${addToCartIcon}</span>
            <span class="is-visually-hidden-mobile">${this.addLabel}</span>
          </button>
        `
        : `
          <button type="button" class="button cart-upsell-product-card__button"
            data-variant-id="${variant.id}"
            data-product-id="${product.id}"
            data-product-title="${productTitle}"
            data-initial-variant-id="${variant.id}"
            data-price-cents="${price}">
            <span class="svg-wrapper">${addToCartIcon}</span>
            <span class="is-visually-hidden-mobile">${this.addLabel}</span>
          </button>
        `
      : `<span class="cart-upsell-product-card__unavailable">${this.soldOutLabel}</span>`;

    return `
      <div class="cart-upsell-product-card"
        data-product-id="${product.id}"
        data-initial-variant-id="${variant?.id || ''}"
        data-product-title="${productTitle}"
        data-price-cents="${price}">
        ${imageUrl ? `
          <a href="${productUrl}" class="cart-upsell-product-card__image">
            <div class="cart-upsell-product-card__image-wrapper" style="--ratio: ${imageRatio};">
              <img src="${imageSizes ? imageSizes.src : imageUrl}"${imageSizes ? ` srcset="${imageSizes.srcset}" sizes="(max-width: 374px) 68px, 80px"` : ''} alt="${imageAlt}" class="cart-upsell-product-card__image-img" loading="lazy">
            </div>
          </a>
        ` : ''}
        <div class="cart-upsell-product-card__content">
          <div>
            <a href="${productUrl}" class="cart-upsell-product-card__title cart-header-typography">
              ${productTitle}
            </a>
            ${priceHtml}
          </div>
          ${actionHtml}
        </div>
      </div>
    `;
  }

  // Same responsive widths as the cart line-item thumbnails (snippets/cart-products.liquid): sharp at 80px on 3x screens.
  cartThumbSizes(url) {
    if (!url || !/(\/cdn\/shop\/|cdn\.shopify\.com\/)/.test(url)) return null;
    try {
      const at = (width) => {
        const u = new URL(url, window.location.origin);
        u.searchParams.set('width', width);
        u.searchParams.delete('height');
        u.searchParams.delete('crop');
        return (url.startsWith('//') ? u.href.replace(/^https?:/, '') : u.href).replace(/"/g, '%22');
      };
      return { src: at(240), srcset: [120, 160, 240, 360, 480].map((w) => `${at(w)} ${w}w`).join(', ') };
    } catch (error) {
      return null;
    }
  }

  getImageRatio(product = null) {
    const ratioSetting = this.dataset.imageRatio || 'square';
    let ratio = 1; // default to square
    
    switch (ratioSetting) {
      case 'portrait':
        ratio = 0.8;
        break;
      case 'square':
        ratio = 1;
        break;
      case 'landscape':
        ratio = 1.25;
        break;
      case 'adapt':
        // If product has aspect_ratio, use it; otherwise default to square
        if (product && product.featured_media && product.featured_media.aspect_ratio) {
          ratio = product.featured_media.aspect_ratio;
        } else if (product && product.featured_image && product.featured_image.aspect_ratio) {
          ratio = product.featured_image.aspect_ratio;
        } else {
          ratio = 1;
        }
        break;
      default:
        ratio = 1;
    }
    
    return ratio;
  }

  renderProductsAsSlider(products, container) {
    // Check if slider container already exists (for related/complementary products)
    let existingSlider = this.querySelector('.cart-upsell-slider');
    let sliderWrapper = null;
    
    // If container is the cart-upsell-slider-wrapper itself, use it directly
    if (container && container.classList.contains('cart-upsell-slider-wrapper')) {
      sliderWrapper = container;
    } else if (existingSlider) {
      // Find the wrapper within the existing slider
      sliderWrapper = existingSlider.querySelector('.cart-upsell-slider-wrapper');
    }
    
    if (sliderWrapper) {
      // Clear existing content (like loading state) and populate with slides
      sliderWrapper.innerHTML = '';
    } else {
      // Create new slider structure for slider mode
      const sliderContainer = document.createElement('div');
      sliderContainer.className = 'cart-upsell-slider';
      sliderContainer.setAttribute('data-cart-upsell-slider', '');

      sliderWrapper = document.createElement('div');
      sliderWrapper.className = 'cart-upsell-slider-wrapper';

      sliderContainer.appendChild(sliderWrapper);

      // Replace container content with slider
      if (container && !container.classList.contains('cart-upsell-slider-wrapper')) {
        container.innerHTML = '';
        container.appendChild(sliderContainer);
      } else {
        // If container doesn't exist or is the wrapper, find the loading state and replace it
        const loadingState = this.querySelector('[ref="loadingState"]');
        const parentContainer = loadingState?.parentElement;
        if (parentContainer) {
          parentContainer.innerHTML = '';
          parentContainer.appendChild(sliderContainer);
        } else {
          // Fallback: append to component
          this.appendChild(sliderContainer);
        }
      }
    }

    // Create slides for each product
    products.forEach((product, index) => {
      const slide = document.createElement('div');
      slide.className = 'cart-upsell-slider-slide cart-upsell__slide';

      const productDiv = document.createElement('div');
      productDiv.className = 'cart-upsell__product';
      productDiv.innerHTML = this.buildProductCardHTML(product, index);

      slide.appendChild(productDiv);
      sliderWrapper.appendChild(slide);
    });

    // Initialize slider
    setTimeout(() => {
      if (!this.isConnected) return;
      this.initSlider();
      this.setupAddToCartButtons();
      this.syncButtonStatesWithCart();
    }, 300);
  }

  setupAddToCartButtons() {
    // Use event delegation - attach listener to the products container, not the whole component
    // This is more resilient to DOM changes
    const productsContainer = this.querySelector('[ref="productsContainer"]') || 
                              this.querySelector('.cart-upsell__products') ||
                              this.querySelector('.cart-upsell-slider-wrapper') ||
                              this;
    
    if (!productsContainer) return;
    
    // Remove old listeners if they exist
    if (this._productsContainer) {
      this._productsContainer.removeEventListener('click', this.handleButtonClick);
      this._productsContainer.removeEventListener('change', this.handleVariantChange);
    }
    
    // Attach to products container
    productsContainer.addEventListener('click', this.handleButtonClick);
    productsContainer.addEventListener('change', this.handleVariantChange);
    this._productsContainer = productsContainer;
    
    // Initialize variant selectors
    this.initializeVariantSelectors();
  }

  initializeVariantSelectors() {
    const variantSelectors = this.querySelectorAll('.cart-upsell-variant-selector');
    
    variantSelectors.forEach(selector => {
      const selects = selector.querySelectorAll('.cart-upsell-variant-selector__select');
      const productCard = selector.closest('.cart-upsell-product-card');
      const button = productCard?.querySelector('.cart-upsell-product-card__button');
      
      if (!button || selects.length === 0) return;
      
      // Set up change listeners for each select
      selects.forEach(select => {
        select.addEventListener('change', () => {
          this.updateVariantForProduct(selector, productCard, button);
        });
      });
      
      // Initialize with current selections
      this.updateVariantForProduct(selector, productCard, button);
    });
  }

  handleVariantChange = (event) => {
    // Handle variant selector changes
    if (event.target.classList.contains('cart-upsell-variant-selector__select')) {
      const selector = event.target.closest('.cart-upsell-variant-selector');
      const productCard = selector?.closest('.cart-upsell-product-card');
      const button = productCard?.querySelector('.cart-upsell-product-card__button');
      
      if (selector && productCard && button) {
        this.updateVariantForProduct(selector, productCard, button);
      }
    }
  }

  updateVariantForProduct(selector, productCard, button) {
    const variantDataElement = selector.querySelector('.cart-upsell-variant-data');
    if (!variantDataElement) return;
    
    try {
      const variantData = JSON.parse(variantDataElement.textContent);
      const selects = selector.querySelectorAll('.cart-upsell-variant-selector__select');
      
      // Get selected values
      const selectedValues = Array.from(selects).map(select => select.value);
      
      // Find matching variant
      const matchingVariant = variantData.variants.find(variant => {
        if (variant.option1 !== selectedValues[0]) return false;
        if (selects.length > 1 && variant.option2 !== selectedValues[1]) return false;
        if (selects.length > 2 && variant.option3 !== selectedValues[2]) return false;
        return true;
      });
      
      if (matchingVariant) {
        // Update button variant ID and analytics price
        button.dataset.variantId = matchingVariant.id.toString();
        button.dataset.priceCents = matchingVariant.price.toString();
        
        // Update price if price container exists
        const priceContainer = productCard.querySelector('[data-price-container]');
        if (priceContainer) {
          const priceCurrent = priceContainer.querySelector('[data-price-current]');
          const priceCompare = priceContainer.querySelector('[data-price-compare]');
          
          if (priceCurrent) {
            priceCurrent.innerHTML = this.formatPriceSplit(matchingVariant.price);

            if (matchingVariant.compare_at_price > matchingVariant.price) {
              priceContainer.classList.remove('cart-upsell-product-card__price--no-compare');
              if (!priceCompare) {
                const compareEl = document.createElement('s');
                compareEl.className = 'cart-upsell-product-card__price-compare cart-secondary-typography';
                compareEl.setAttribute('data-price-compare', '');
                compareEl.innerHTML =
                  '<span class="cart-upsell-product-card__compare-inner">' +
                  this.formatPriceSplit(matchingVariant.compare_at_price) +
                  '</span>';
                priceContainer.appendChild(compareEl);
              } else {
                priceCompare.innerHTML =
                  '<span class="cart-upsell-product-card__compare-inner">' +
                  this.formatPriceSplit(matchingVariant.compare_at_price) +
                  '</span>';
              }
            } else {
              priceContainer.classList.add('cart-upsell-product-card__price--no-compare');
              if (priceCompare) {
                priceCompare.remove();
              }
            }
          }
        }
        
        // Update button availability
        if (matchingVariant.available) {
          button.disabled = false;
          button.classList.remove('unavailable');
          const unavailableText = productCard.querySelector('.cart-upsell-product-card__unavailable');
          if (unavailableText) {
            unavailableText.remove();
          }
        } else {
          button.disabled = true;
          button.classList.add('unavailable');
          // Show unavailable message if not already present
          if (!productCard.querySelector('.cart-upsell-product-card__unavailable')) {
            const unavailableText = document.createElement('span');
            unavailableText.className = 'cart-upsell-product-card__unavailable';
            unavailableText.textContent = this.soldOutLabel;
            button.parentNode.insertBefore(unavailableText, button);
          }
        }
      }
    } catch (error) {
      console.error('Error updating variant:', error);
    }
  }

  handleButtonClick(event) {
    // Check if the click was on an add to cart button or its children (like SVG)
    let button = event.target.closest('.cart-upsell-product-card__button');
    
    // If click was on SVG or span inside button, find the button
    if (!button && (event.target.closest('svg') || event.target.closest('.svg-wrapper'))) {
      button = event.target.closest('.cart-upsell-product-card').querySelector('.cart-upsell-product-card__button');
    }
    
    if (!button) return;

    // Don't process if button is already disabled or processing
    if (cartMutation.busy || button.dataset.processing === 'true' || button.disabled || button.classList.contains('processing') || button.classList.contains('added')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    
    // Check if product has variants - open popup instead of direct add
    if (button.dataset.hasVariants === 'true') {
      this.openVariantPopup(button);
    } else {
      this.handleAddToCart(button).catch(() => {});
    }
  }

  // Keep feedback inside the existing button without replacing its sizing content.
  setUpsellButtonFeedback(button, state) {
    if (!button) return;
    if (state === 'idle') {
      button.querySelector('.sp-upsell-feedback')?.remove();
      delete button.dataset.upsellFeedback;
      button.removeAttribute('aria-busy');
      if (button._upsellLabel !== undefined) {
        button.setAttribute('aria-label', button._upsellLabel);
        delete button._upsellLabel;
      }
      return;
    }
    if (!button.querySelector('.sp-upsell-feedback')) {
      const feedback = document.createElement('span');
      feedback.className = 'sp-upsell-feedback';
      feedback.setAttribute('aria-hidden', 'true');
      feedback.innerHTML = '<span class="sp-upsell-dots"><i></i><i></i><i></i></span><svg class="sp-upsell-check" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.6"/><path d="M7 12l3.2 3.2L17 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      button.append(feedback);
    }
    if (button._upsellLabel === undefined) {
      button._upsellLabel = button.getAttribute('aria-label') || `${this.addLabel}: ${button.dataset.productTitle || ''}`;
    }
    button.dataset.upsellFeedback = state;
    button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    button.setAttribute('aria-label', state === 'loading' ? 'Adding to cart' : 'Added to cart');
  }

  async handleAddToCart(button) {
    if (button?.dataset?.processing === 'true') throw new Error('This item is already being added.');
    // Get variant data from button (button might be a temporary element not in DOM)
    const variantId = button?.dataset?.variantId;
    const productId = button?.dataset?.productId;
    const productTitle = button?.dataset?.productTitle;

    if (!variantId) {
      console.error('Variant ID is required');
      return Promise.reject(new Error('Variant ID is required'));
    }

    // For temporary buttons (not in DOM), we don't need to check isConnected
    const isTemporaryButton = !button.isConnected;
    const originalHTML = isTemporaryButton ? null : button.innerHTML;

    const token = cartMutation.begin();
    if (!token) throw new Error('Cart is updating. Please wait before adding another item.');
    button.dataset.processing = 'true';
    try {
    // For temporary buttons (from popup), we don't modify the button state
    // Instead, we'll update the popup button state separately
    if (!isTemporaryButton) {
      // Ensure this specific button is the only one being processed
      button.dataset.processing = 'true';
      
      // Disable ONLY this button during request
      button.disabled = true;
      button.classList.add('processing');
      this.setUpsellButtonFeedback(button, 'loading');
      

    }
    
      // Get section IDs from cart items components
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = Array.from(cartItemsComponents)
        .map(component => component.dataset.sectionId)
        .filter(Boolean);

      // Only server-signed attribution is attached. Failure leaves cart behavior intact.
      const attributionEventId = this.createUpsellEventId();
      let attribution = null;
      if (this.isUpsellAnalyticsAllowed()) {
        try {
          attribution = await this.requestUpsellAttribution(attributionEventId, [{
            product_id: productId?.toString(),
            variant_id: variantId.toString(),
            price_cents: Math.max(0, Math.round(Number(button?.dataset?.priceCents || 0))),
            quantity: 1,
            position: 0
          }]);
        } catch (_) {
          attribution = null;
        }
      }

      // Use FormData like the product form does to include sections in response
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (attribution) {
        formData.append('properties[_sp_cart_upsell]', 'true');
        formData.append('properties[_sp_cart_upsell_event_id]', attributionEventId);
        formData.append('properties[_sp_cart_upsell_location]', attribution.upsell_location);
        formData.append('properties[_sp_cart_upsell_token]', attribution.attribution_token);
      }
      if (sectionIds.length > 0) {
        formData.append('sections', sectionIds.join(','));
      }

      const response = await cartMutation.fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to add to cart: ${response.status}`);
      }

      const data = await response.json();

      // Check if there was an error in the response
      if (data.status) {
        throw new Error(data.message || 'Failed to add product to cart');
      }

      const addedItem = Array.isArray(data.items) ? data.items[0] : data;
      const addedPriceCents = Number(
        addedItem?.final_price ?? addedItem?.price ?? button?.dataset?.priceCents ?? 0
      );
      const matchingCard = Array.from(this.querySelectorAll('.cart-upsell-product-card')).find((card) =>
        card.querySelector('.cart-upsell-product-card__button')?.dataset.productId === productId?.toString()
      );
      const itemIndex = matchingCard
        ? Array.from(this.querySelectorAll('.cart-upsell-product-card')).indexOf(matchingCard)
        : 0;

      if (attribution) {
        this.publishUpsellAnalyticsEvent('cart_upsell_add', {
          attribution,
          items: [{
            product_id: productId?.toString() || addedItem?.product_id?.toString(),
            variant_id: variantId.toString(),
            price_cents: Math.max(0, Math.round(addedPriceCents)),
            quantity: Number(addedItem?.quantity || 1),
            position: itemIndex
          }]
        });
      }

      // /add.js reports added lines, not the total cart count.
      const authoritativeCart = await cartMutation.read();
      authoritativeCart.sections = data.sections;
      // Let the confirmed check play before section morphing removes the card.
      if (!isTemporaryButton && button.isConnected) {
        this.setUpsellButtonFeedback(button, 'success');
        await new Promise(resolve => setTimeout(resolve, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 100 : 500));
      }
      await cartMutation.commit(token, authoritativeCart, 'cart-upsell-component', true, {
        variantId: variantId.toString(), productId: productId?.toString(),
      });

      // Show temporary success feedback (only for buttons in DOM)
      if (!isTemporaryButton) {
        this.setUpsellButtonFeedback(button, 'success');
        button.classList.add('processing'); // Keep processing class temporarily
        
        // Hide this product from upsells (only disable this specific button, not the whole card)
        const productCard = button.closest('.cart-upsell-product-card');
        if (productCard) {
          productCard.classList.add('product-added');
        }

        // Remove processing flag
        button.classList.remove('processing');
        delete button.dataset.processing;
      }
      
      // Verify product is actually in cart and sync all button states
      // Wait a bit for cart to update before syncing - this will properly set all button states
      setTimeout(() => {
        this.syncButtonStatesWithCart();
        // Double-check after another delay to ensure component is hidden if needed
        setTimeout(() => {
          this.syncButtonStatesWithCart();
        }, 200);
      }, 300);

    } catch (error) {
      const recovered = await cartMutation.recover(token);
      
      // Re-enable this button on error (only for buttons in DOM)
      if (!isTemporaryButton && button && button.isConnected) {
        button.disabled = false;
        button.classList.remove('processing');
        if (originalHTML) {
          button.innerHTML = originalHTML;
          this.setUpsellButtonFeedback(button, 'idle');
        }
        delete button.dataset.processing;
      }
      
      // Show error message
      // SP fix: inline cart status instead of a blocking native alert().
      if (!isTemporaryButton && recovered) cartMutation.notice(error?.timeout
        ? 'The cart took too long to respond, so we re-checked it. Please review your bag before trying again.'
        : 'We could not confirm that item was added, so we re-checked your bag. Please review it before trying again.');
      
      // Re-throw error so promise chain can handle it
      throw error;
    } finally {
      delete button.dataset.processing;
      await cartMutation.end(token);
    }
  }

  async openVariantPopup(button) {
    const productId = button.dataset.productId;
    const productHandle = button.dataset.productHandle;
    const productTitle = button.dataset.productTitle;
    
    if (!productId) {
      console.error('Product ID is required');
      return;
    }

    // Get or create popup
    let popup = document.getElementById('cart-upsell-variant-popup');
    if (!popup) {
      // Popup should be rendered in Liquid template, but if it doesn't exist, create it
      popup = document.createElement('dialog');
      popup.id = 'cart-upsell-variant-popup';
      popup.className = 'cart-upsell-variant-popup dialog-modal';
      popup.setAttribute('scroll-lock', '');
      
      // Create popup structure
      const content = document.createElement('div');
      content.className = 'cart-upsell-variant-popup__content';
      
      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'button button-unstyled close-button cart-upsell-variant-popup__close';
      closeBtn.setAttribute('data-popup-close', '');
      closeBtn.innerHTML = '<span class="svg-wrapper">✕</span>';
      
      const header = document.createElement('div');
      header.className = 'cart-upsell-variant-popup__header';
      header.innerHTML = '<h3 class="cart-upsell-variant-popup__title" data-popup-title></h3><div class="cart-upsell-variant-popup__price" data-popup-price></div>';
      
      const body = document.createElement('div');
      body.className = 'cart-upsell-variant-popup__body';
      body.setAttribute('data-popup-body', '');
      
      const footer = document.createElement('div');
      footer.className = 'cart-upsell-variant-popup__footer';
      footer.innerHTML = '<button type="button" class="button cart-upsell-variant-popup__add-button" data-popup-add-button><span class="svg-wrapper">+</span><span>Add to cart</span></button>';
      
      content.appendChild(closeBtn);
      content.appendChild(header);
      content.appendChild(body);
      content.appendChild(footer);
      popup.appendChild(content);
      
      document.body.appendChild(popup);
    }

    // Fetch product data
    try {
      // Try to get product handle from data attribute or product link
      let handle = productHandle;
      
      if (!handle) {
        const productCard = button.closest('.cart-upsell-product-card');
        const productLink = productCard?.querySelector('.cart-upsell-product-card__title, .cart-upsell-product-card__image');
        
        if (productLink && productLink.href) {
          const url = new URL(productLink.href);
          handle = url.pathname.split('/products/').pop()?.split('?')[0];
        }
      }
      
      if (!handle) {
        throw new Error('Product handle not found');
      }
      
      const productUrl = `/products/${handle}.js`;
      const response = await fetch(productUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status}`);
      }
      const product = await response.json();

      // Update popup content
      const titleEl = popup.querySelector('[data-popup-title]');
      const priceEl = popup.querySelector('[data-popup-price]');
      const bodyEl = popup.querySelector('[data-popup-body]');
      const addButton = popup.querySelector('[data-popup-add-button]');

      if (titleEl) {
        titleEl.textContent = product.title;
      }

      // Set initial price from an explicitly available variant
      const firstVariant = this.getFirstAvailableVariant(product);
      if (!firstVariant) {
        throw new Error('No available variant found for upsell product');
      }
      if (priceEl) {
        const price = this.formatPriceSplit(firstVariant.price);
        const comparePrice = firstVariant.compare_at_price > firstVariant.price 
          ? this.formatPriceSplit(firstVariant.compare_at_price) 
          : null;
        
        priceEl.innerHTML = comparePrice 
          ? `<span class="cart-upsell-variant-popup__price-current">${price}</span><span class="cart-upsell-variant-popup__price-compare">${comparePrice}</span>`
          : `<span class="cart-upsell-variant-popup__price-current">${price}</span>`;
      }

      // Create variant selector
      if (bodyEl && product.variants && product.variants.length > 1) {
        const variantSelector = this.createVariantSelectorForPopup(product, popup);
        
        if (variantSelector && variantSelector.children.length > 0) {
          bodyEl.innerHTML = '';
          bodyEl.appendChild(variantSelector);
        } else {
          bodyEl.innerHTML = '<p>No variants available for selection.</p>';
        }
      } else if (!bodyEl) {
        console.error('Popup body element not found');
      } else if (!product.variants || product.variants.length <= 1) {
        if (bodyEl) {
          bodyEl.innerHTML = '<p>This product has no variants to select.</p>';
        }
      }

      delete popup.dataset.spProcessing;
      // Set up add button
      if (addButton) {
        popup._spAddMarkup ||= addButton.innerHTML;
        addButton.dataset.productId = productId;
        addButton.dataset.productTitle = productTitle;
        addButton.dataset.variantId = firstVariant.id.toString();
        
        // Remove any existing event listeners by cloning (but keep the same reference)
        const clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          if (popup.dataset.spProcessing === 'true') return;
          // Get current variant ID from dataset (updated when variant changes)
          const currentButton = popup.querySelector('[data-popup-add-button]');
          const selectedVariantId = currentButton?.dataset.variantId;
          
          if (!selectedVariantId) {
            console.error('No variant ID found');
            this.showPopupError(popup, 'Please select a variant.');
            return;
          }
          
          // Disable button during processing
          if (currentButton) {
            this.showPopupError(popup, '');
            popup.dataset.spProcessing = 'true';
            currentButton.disabled = true;
            currentButton.setAttribute('aria-busy', 'true');
            const originalText = currentButton.innerHTML;
            currentButton.dataset.feedback = 'loading';
            currentButton.innerHTML = '<span class="sp-photo-dots" aria-hidden="true"><i></i><i></i><i></i></span><span class="visually-hidden">Adding to cart</span>';
            
            // Create a temporary button with variant ID for handleAddToCart
            const tempButton = document.createElement('button');
            tempButton.dataset.variantId = selectedVariantId;
            tempButton.dataset.productId = productId;
            tempButton.dataset.productTitle = productTitle;
            tempButton.dataset.priceCents = currentButton.dataset.priceCents;
            
            // Call handleAddToCart
            this.handleAddToCart(tempButton).then(() => {
              currentButton.removeAttribute('aria-busy');
              currentButton.dataset.feedback = 'success';
              currentButton.innerHTML = '<span class="sp-photo-check" aria-hidden="true">✓</span><span class="visually-hidden">Added to cart</span>';
              setTimeout(() => this.closeVariantPopup(), matchMedia('(prefers-reduced-motion: reduce)').matches ? 120 : 550);
            }).catch((error) => {
              // Re-enable button on error
              if (currentButton) {
                delete popup.dataset.spProcessing;
                delete currentButton.dataset.feedback;
                currentButton.removeAttribute('aria-busy');
                currentButton.disabled = false;
                currentButton.innerHTML = originalText;
              }
              console.error('Error adding to cart:', error);
              this.showPopupError(popup, error?.timeout ? 'The cart took too long to respond, so we re-checked it. Please review your bag before trying again.' : (error?.message && error.message.length < 120 ? error.message : 'Unable to add product to cart. Please try again.'));
            });
          }
        };
        
        // Remove old listener if exists
        addButton.replaceWith(addButton.cloneNode(true));
        const newAddButton = popup.querySelector('[data-popup-add-button]');
        if (newAddButton) {
          newAddButton.innerHTML = popup._spAddMarkup;
          delete newAddButton.dataset.feedback;
          newAddButton.removeAttribute('aria-busy');
          newAddButton.dataset.productId = productId;
          newAddButton.dataset.productTitle = productTitle;
          newAddButton.dataset.variantId = firstVariant.id.toString();
          newAddButton.disabled = false;
          newAddButton.addEventListener('click', clickHandler);
        }
      }

      this.updateVariantInPopup(product, popup);
      popup.setAttribute('aria-labelledby', 'sp-photo-popup-title');
      titleEl.id = 'sp-photo-popup-title';
      // Set up close button
      const closeButton = popup.querySelector('[data-popup-close]');
      if (closeButton) {
        closeButton.onclick = () => this.closeVariantPopup();
      }

      popup.oncancel = (event) => { if (popup.querySelector('[data-feedback="loading"]')) event.preventDefault(); };
      // Close on backdrop click
      popup.onclick = (e) => {
        if (e.target === popup) {
          this.closeVariantPopup();
        }
      };

      // Show popup
      popup.showModal();
    } catch (error) {
      console.error('Error opening variant popup:', error);
      cartMutation.notice('Unable to load product variants. Please try again.');
    }
  }

  // SP fix: inline, accessible error inside the variant popup (replaces alert()).
  showPopupError(popup, message) {
    if (!popup) return;
    let el = popup.querySelector('[data-popup-error]');
    if (!message) { if (el) { el.hidden = true; el.textContent = ''; } return; }
    if (!el) {
      el = document.createElement('p');
      el.dataset.popupError = '';
      el.className = 'cart-upsell-variant-popup__error';
      el.setAttribute('role', 'alert');
      el.style.cssText = 'margin:0 0 8px;color:#c4001d;font-size:13px;line-height:1.35;';
      const footer = popup.querySelector('.cart-upsell-variant-popup__footer');
      if (footer) footer.prepend(el); else popup.append(el);
    }
    el.textContent = message;
    el.hidden = false;
  }

  createVariantSelectorForPopup(product, popup) {
    const container = document.createElement('div');
    container.className = 'cart-upsell-variant-popup__variants sp-photo-variants';
    const label = document.createElement('p');
    label.className = 'sp-photo-variants__label';
    label.textContent = 'Choose your variant';
    const grid = document.createElement('div');
    grid.className = 'sp-photo-variants__grid';
    grid.setAttribute('role', 'group');
    grid.setAttribute('aria-label', 'Product variants');
    container.append(label, grid);
    popup._spPhotoProduct = product;
    popup.dataset.selectedVariant = String(this.getFirstAvailableVariant(product)?.id || '');
    for (const variant of product.variants || []) {
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'sp-photo-variants__tile';
      tile.dataset.variantId = String(variant.id);
      tile.disabled = variant.available !== true;
      tile.setAttribute('aria-label', `${variant.title}${tile.disabled ? ' — Sold out' : ''}`);
      tile.setAttribute('aria-pressed', String(String(variant.id) === popup.dataset.selectedVariant));
      const src = variant.featured_image?.src || (typeof variant.featured_image === 'string' ? variant.featured_image : null);
      if (src && /^(https?:)?\/\//.test(src)) {
        const image = document.createElement('img');
        image.src = src;
        image.alt = `${product.title} — ${variant.title}`;
        image.width = 160; image.height = 160;
        image.decoding = 'async';
        image.addEventListener('error', () => { image.remove(); tile.dataset.noImage = 'true'; });
        tile.append(image);
      } else {
        const fallback = document.createElement('span');
        fallback.className = 'sp-photo-variants__missing';
        fallback.textContent = 'No variant image';
        tile.append(fallback);
      }
      const name = document.createElement('span');
      name.className = 'sp-photo-variants__name';
      name.textContent = variant.title;
      const status = document.createElement('span');
      status.className = 'sp-photo-variants__status';
      status.textContent = tile.disabled ? 'Sold out' : (String(variant.id) === popup.dataset.selectedVariant ? 'Selected' : 'Available');
      tile.append(name, status);
      tile.addEventListener('click', () => {
        if (popup.dataset.spProcessing === 'true' || tile.disabled) return;
        popup.dataset.selectedVariant = String(variant.id);
        this.updateVariantInPopup(product, popup);
      });
      grid.append(tile);
    }
    return container;
  }

  updateVariantInPopup(product, popup) {
    const variant = product.variants.find(v => String(v.id) === popup.dataset.selectedVariant);
    const button = popup.querySelector('[data-popup-add-button]');
    if (!variant || variant.available !== true) {
      if (button) { button.disabled = true; delete button.dataset.variantId; }
      return;
    }
    for (const tile of popup.querySelectorAll('.sp-photo-variants__tile')) {
      const selected = tile.dataset.variantId === String(variant.id);
      tile.setAttribute('aria-pressed', String(selected));
      const status = tile.querySelector('.sp-photo-variants__status');
      status.textContent = tile.disabled ? 'Sold out' : (selected ? 'Selected' : 'Available');
    }
    const price = popup.querySelector('[data-popup-price]');
    if (price) price.innerHTML = `<span class="cart-upsell-variant-popup__price-current">${this.formatPriceSplit(variant.price)}</span>` + (variant.compare_at_price > variant.price ? `<s class="cart-upsell-variant-popup__price-compare">${this.formatPriceSplit(variant.compare_at_price)}</s>` : '');
    if (button) {
      button.dataset.variantId = String(variant.id);
      button.dataset.priceCents = String(variant.price);
      button.disabled = popup.dataset.spProcessing === 'true';
      button.setAttribute('aria-label', `Add ${variant.title} to cart`);
      const text = button.querySelector('[data-popup-add-label]');
      if (text) text.textContent = `Add ${variant.title} to cart`;
    }
  }

  closeVariantPopup() {
    const popup = document.getElementById('cart-upsell-variant-popup');
    if (popup && popup.open) {
      if (popup.querySelector('[data-feedback="loading"]')) return;
      popup.classList.add('dialog-closing');
      setTimeout(() => {
        popup.close();
        popup.classList.remove('dialog-closing');
      }, 300);
    }
  }

  formatMoney(cents) {
    const theme = typeof window.Theme !== 'undefined'
      ? window.Theme
      : (typeof Theme !== 'undefined' ? Theme : null);
    const amount = Number(cents || 0) / 100;
    const formattedAmount = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);

    if ((window.Shopify?.currency?.active || theme?.currency) === 'AED' && !document.documentElement.lang.toLowerCase().startsWith('ar')) {
      return `${formattedAmount} Dhs`;
    }

    if (theme?.currencyPrefix || theme?.currencySuffix) {
      return `${theme.currencyPrefix || ''}${formattedAmount}${theme.currencySuffix || ''}`.trim();
    }

    if (theme?.moneyFormat) {
      return theme.moneyFormat.replace('{{amount}}', formattedAmount);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: theme?.currency || 'USD',
      minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  formatPriceSplit(cents) {
    const theme = typeof window.Theme !== 'undefined'
      ? window.Theme
      : (typeof Theme !== 'undefined' ? Theme : null);

    if ((window.Shopify?.currency?.active || theme?.currency) === 'AED' && !document.documentElement.lang.toLowerCase().startsWith('ar')) {
      return `<span class="money">${this.formatMoney(cents)}</span>`;
    }

    if (theme?.formatPriceSplitHTML) {
      return theme.formatPriceSplitHTML(cents, false, 'cart');
    }
    return this.formatMoney(cents);
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}

